import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import {
    getProductsId, getGridElements, getCategoryAttachments, getCategory, getFilters,
} from '../../../store/category-page.selectors';
import {
    GridDefault,
    GridElement,
    GridNonDefault,
    GridStudio,
    PageProductsLoad,
    SeparatorHiddenGridElements,
    SizesToFilter,
} from '../models/grid.models';

import { Category } from '@core/domain/models/category.models';
import { getAttachmentByPath } from '@features/category/application/services/category.utils';
import { MediasPublicConstants } from '@features/products/medias';
import { ProductsArrayPublicModels } from '@features/products/productsArray';
import { isDefined } from '@frontend/common/utils';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { CategoryFilterState } from '@pages/category/public_api';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { AttachmentsEnums } from '@shared/header/services/attachments.enums';
import { GridType, GridTypes } from '@shared/models/grid-type.model';
import { Position, ProductViewConfig } from '@shared/product/components/product-view/models/product-view-config.model';

@Injectable()
export class CategoryGridService {
    public readonly getProductsId$: Observable<number[]> = this.store.select(getProductsId);

    public readonly getGridType$: Observable<GridType> = this.store.select(getGridType);

    public readonly getGridElements$: Observable<GridElement[]> = this.store.select(getGridElements);

    public readonly categoryAttachments$: Observable<any> = this.store.select(getCategoryAttachments);

    public readonly isMobile$: Observable<boolean> = this.store.select(getResolution)
        .pipe(map((resolutions: Resolutions) => resolutions === Resolutions.MOBILE));

    public readonly getCategory$: Observable<Category> = this.store.select(getCategory);

    public readonly getFilters$: Observable<CategoryFilterState> = this.store.select(getFilters);

    constructor(
        private readonly store: Store,
    ) { }

    public mapStateGridDefault(): Observable<GridDefault> {
        return combineLatest([
            this.getProductsId$.pipe(filter(value => isDefined(value))),
            this.getFilters$,
            this.getCategory$,
            this.getGridType$,
            this.getGridElements$.pipe(filter(value => value?.length > 0)),
        ]).pipe(
            take(1),
            map(([productsId, filters, category, gridType, gridElements]) => {
                return CategoryGridService.buildGridDefaultState(productsId, filters, category, gridType, gridElements);
            }),
        );
    }

    public static buildGridDefaultState(
        productsId: number[],
        filters: any,
        category: any,
        gridType: GridTypes,
        gridElements: GridElement[]
    ): GridDefault {
        const pageSize = CategoryGridService.getPageSize(gridType);

        // obtenemos los IDS a incluir a través de attachments shopBySize
        const filterByAttachment = getAttachmentByPath(category, AttachmentsEnums.SHOP_BY_SIZE);
        // obtenemos los IDS a incluir a través del attachment de talla SIZE_?
        const filterBySizeAttachment = CategoryGridService.getProductIdsByAttachmentNameAndSizeFilter(
            category.attachments,
            filters.filters.sizeGroupFilter
        );
        const productsBySize = filterByAttachment
            ? CategoryGridService.filterProductIdsByArraySizes(filterByAttachment.name?.split(','), filters.filters.sizeFilter)
            : [];

        // obtenemos los IDS que estan dentro del template para excluir
        const hideSeparators = category?.attachments?.find(
            (attachment) => attachment.name === AttachmentsEnums.HIDE_SEPARATORS
        );

        const excludedGridElements: number[] = gridElements.reduce((els, el) => {
            const hideSeparator = hideSeparators && el.template in SeparatorHiddenGridElements;
            if (hideSeparator) {
                return els.concat(el.ccIds);
            }
            return els;
        }, []);

        // filtramos los ids totales en base a las necesidades de negocio
        const idsFiltered: number[] = productsId.reduce((ids, id) => {
            const attachmentMatch = !filterByAttachment || productsBySize.includes(id);
            const sizeAttachmentMatch = !filterBySizeAttachment || filterBySizeAttachment.includes(id);
            const isSizeMatch = attachmentMatch && sizeAttachmentMatch;
            const isNotExcluded = !excludedGridElements.includes(id);

            if (isSizeMatch && isNotExcluded) {
                ids.push(id);
            }
            return ids;
        }, []);

        const initPage = CategoryGridService.sliceIdsWithPageSize(gridType, idsFiltered, 0);

        return {
            totalPagesIds: Math.ceil(idsFiltered.length / pageSize),
            idsFiltered: idsFiltered,
            pageProductIds: initPage.ids,
            currentPage: initPage.page,
        };
    }

    public mapStateGridStudio(): Observable<GridStudio> {
        return this.getGridElements$.pipe(
            filter(value => value?.length > 0),
            take(1),
            map((gridElements: GridElement[]) => {
                return CategoryGridService.buildGridStudioState(gridElements);
            }),
        );
    }

    private static getProductIdsByAttachmentNameAndSizeFilter(attachments, sizeGroupFilters): number[] {
        if (attachments && sizeGroupFilters) {
            const validAttachments = attachments.filter(att => SizesToFilter.includes(att.name));

            let allProductIds = [];

            // biome-ignore lint/complexity/noForEach: <explanation>
            validAttachments.forEach(attachment => {
                const sizeGroupFilter = sizeGroupFilters.find(sizeFilter => sizeFilter.name === attachment.name);

                if (sizeGroupFilter) {
                    allProductIds = allProductIds.concat(sizeGroupFilter.productIds);
                }
            });
            return allProductIds.length ? allProductIds : null;
        }
    }

    public static buildGridStudioState(gridElements): GridStudio {
        const pageSizeStudio = CategoryGridService.getPageSize(GridTypes.ZARA_STUDIO);
        const initPage = CategoryGridService.sliceIdsWithPageSize(GridTypes.ZARA_STUDIO, gridElements, 0);
        return {
            totalPagesGridElements: Math.ceil(gridElements.length / pageSizeStudio),
            gridElementsFiltered: gridElements,
            pageGridElements: initPage.ids,
            currentPage: initPage.page,
        };
    }

    public static mapStateGridNewCollection(
        data: Array<{ categoryGrids: ProductsArrayPublicModels.PublicCategoryPlatformStudio; categoryId: string }>,
        gridType: GridTypes
    ): GridNonDefault {
        const categoryProductsFlat = data.reduce((accumulatedItems, item) => {
            const filters = { filters: item.categoryGrids.filters };
            const { productIds, gridElements } = item.categoryGrids;

            const mapDefault = CategoryGridService.buildGridDefaultState(
                productIds,
                filters,
                item.categoryId,
                gridType,
                gridElements
            );
            const pageSizeDefault = CategoryGridService.getPageSize(gridType);
            const idsFiltered = accumulatedItems?.default?.idsFiltered
                ? accumulatedItems.default.idsFiltered.concat(mapDefault.idsFiltered) : mapDefault.idsFiltered;
            const initPageDefault = CategoryGridService.sliceIdsWithPageSize(gridType, idsFiltered, 0);

            const pageSizeStudio = CategoryGridService.getPageSize(GridTypes.ZARA_STUDIO);
            const gridElementsFiltered = accumulatedItems?.studio?.gridElementsFiltered
                ? accumulatedItems.studio.gridElementsFiltered.concat(gridElements) : gridElements;
            const initPageStudio = CategoryGridService.sliceIdsWithPageSize(GridTypes.ZARA_STUDIO, gridElementsFiltered, 0);

            return {
                default: {
                    totalPagesIds: Math.ceil(idsFiltered.length / pageSizeDefault),
                    idsFiltered,
                    pageProductIds: initPageDefault.ids,
                    currentPage: 1,
                },
                studio: {
                    totalPagesGridElements: Math.ceil(gridElementsFiltered.length / pageSizeStudio),
                    gridElementsFiltered,
                    pageGridElements: initPageStudio.ids,
                    currentPage: 1,
                },
            };
        }, { default: undefined, studio: undefined });

        return categoryProductsFlat;
    }

    private static filterProductIdsByArraySizes(
        arraySizes: string[],
        sizeFilter: any // ProductFilterOrderItem[]
    ): number[] {
        const skipIfSizeNotIncluded = (sizes, size) => arraySizes.includes(size?.name) ? sizes.concat(size) : sizes;
        const sizesIncluded = sizeFilter.reduce(skipIfSizeNotIncluded, []);
        if (sizesIncluded.length === 1) {
            return sizesIncluded[0].productIds;
        }
        return sizesIncluded.reduce((acc, curr) => ({ productIds: acc.productIds.concat(curr.productIds) })).productIds;
    }

    public static sliceIdsWithPageSize(
        gridType: GridType,
        allIds,
        currentPage: number,
        pageProductIds = 0
    ): { ids: any; page: number } {
        // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
        let page;
        // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
        let startIndex;
        // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
        let endIndex;
        const pageSize = CategoryGridService.getPageSize(gridType);

        if (pageProductIds > 0) {
            startIndex = pageProductIds;
            endIndex = pageProductIds + pageSize;
            const totalPages = Math.ceil(allIds.length / pageSize);
            const remainingPages = Math.ceil((allIds.length - endIndex) / pageSize);
            page = totalPages - remainingPages;
        } else {
            startIndex = currentPage * pageSize;
            endIndex = (currentPage + 1) * pageSize;
            page = currentPage + 1;
        }

        return { ids: allIds.slice(startIndex, endIndex), page };
    }

    // Se puede optimizar la carga en vista Mobile (solo son skeletors no tiene mejora de performance)
    public static getPageSize(gridType: GridTypes): number {
        switch (gridType) {
            case GridTypes.GRID_16:
                return PageProductsLoad.GRID_16;
            case GridTypes.GRID_4:
                return PageProductsLoad.GRID_4;
            default:
                return PageProductsLoad.ZARA_STUDIO;
        }
    }

    public static mapTemplateConfig(
        catalogStyles,
        place,
        location = MediasPublicConstants.PublicMediaLocationType.category_page,
        clickable = true
    ): ProductViewConfig {
        return {
            clickable,
            media: {
                ratio: '3-4',
            },
            location,
            imageType: undefined,
            showDescription: false,
            showTags: true,
            showName: true,
            showPrice: true,
            showQuickly: true,
            showColors: true,
            textPosition: Position.bottom,
            showDiscount: true,
            showMadeInExclusive: true,
            showContentProjected: false,
            styles: {
                backgroundColor: catalogStyles?.backgroundColor,
                fontColor: catalogStyles?.fontColor,
                offerPrice: catalogStyles?.offerPrice,
                colorSelector: catalogStyles?.colorSelector,
                cardColor: catalogStyles?.backgroundColor,
            },
            place,
        };
    }
}
